import { Box, Button, IconButton, Tooltip } from '@mui/material';
import {
    getItemsPicklist,
    getZonesPicklist,
    resetAssetsPicklistStatus,
    resetItemsPicklistStatus,
    resetZonesPicklistStatus,
    selectAssetsPicklist,
    selectZonesPicklist,
} from 'features/picklists/pickListsSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { MdAdd, MdEdit } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import HotspotAnswerField from './HotspotAnswerField';
import HotspotPairsTargetNode from './HotspotPairsTargetNode';
import HotspotPanoDataForm from './HotspotPanoDataForm';

const HotspotAnswerGroupType = ({
    node,
    nodes,
    values,
    setFieldValue,
    nodeFormStyles,
    onDragEnd,
    handleRemove,
    handleRemoveOptionsSelected,
}) => {
    const assetList = useSelector(selectAssetsPicklist);
    const zoneList = useSelector(selectZonesPicklist);

    const dispatch = useDispatch();

    const [openHotspotOptionForm, setOpenHotspotOptionForm] = useState(false);

    const fetchAssetName = useCallback(
        (assetId) => {
            if (assetId) {
                const filterAssetName = assetList.filter((val) => {
                    return assetId === val.asset_id ? val.display_name : null;
                });
                return filterAssetName?.[0]?.display_name;
            }
        },
        [assetList]
    );

    const fetchZoneName = useCallback(
        (zoneId) => {
            if (zoneId) {
                const filterZoneName = zoneList.filter((val) => {
                    return zoneId === val.zone_id ? val.display_name : null;
                });
                return filterZoneName?.[0]?.display_name;
            }
        },
        [zoneList]
    );

    const hotspotOpt = useMemo(() => {
        if (!(Array.isArray(values?.hotspot_options) && values.hotspot_options?.length)) {
            return null;
        }

        const firstHotpost = values.hotspot_options?.[0];

        return {
            ...firstHotpost,
            asset_name: fetchAssetName(firstHotpost?.asset_id),
            zone_name: fetchZoneName(firstHotpost?.zone_id),
        };
    }, [fetchAssetName, fetchZoneName, values.hotspot_options]);

    const handleAddHotspotOption = () => {
        if (hotspotOpt?.asset_id && hotspotOpt?.zone_id) {
            const hotspotOpts = Array.isArray(values.hotspot_options) ? [...values.hotspot_options] : [];
            hotspotOpts.push({
                action: {
                    id: '',
                    type: 'node',
                },
                hotspot_id: Number(hotspotOpts?.length),
                asset_id: hotspotOpt.asset_id,
                zone_id: hotspotOpt.zone_id,
                item_id: null,
                option_text: uuidv4(),
                answer_value: '',
            });
            setFieldValue('hotspot_options', hotspotOpts);
        }
    };

    const handleAddHotspotPairTarget = (hotspot_target_combinations, setFieldValue) => {
        if (!Array.isArray(hotspot_target_combinations)) {
            hotspot_target_combinations = [];
        }
        const hotspotPairs = [...hotspot_target_combinations];
        hotspotPairs.push({
            action: {
                id: '',
                type: 'node',
            },
            hotspot_pair_id: Number(hotspotPairs?.length),
            options_required: [],
            answer_value: '',
        });
        setFieldValue('hotspot_target_combinations', hotspotPairs);
    };

    useEffect(() => {
        dispatch(resetAssetsPicklistStatus());
        dispatch(resetZonesPicklistStatus());
        dispatch(resetItemsPicklistStatus());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (Array.isArray(node?.hotspot_options) && node?.hotspot_options.length) {
            dispatch(getZonesPicklist({ assetId: node?.hotspot_options?.[0]?.asset_id }));
            dispatch(getItemsPicklist({ zoneId: node?.hotspot_options?.[0]?.zone_id }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node?.hotspot_options]);

    return (
        <>
            {hotspotOpt?.asset_name && hotspotOpt?.zone_name ? (
                <Box my={1}>
                    {`${hotspotOpt?.asset_name} - ${hotspotOpt?.zone_name}`}
                    <Tooltip title="Edit asset/zone">
                        <IconButton
                            className={nodeFormStyles.addButtonIcon}
                            onClick={() => setOpenHotspotOptionForm(true)}
                            size="small"
                            sx={{ ml: 1 }}
                        >
                            <MdEdit className="react-icon" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : (
                <Button sx={{ mt: 1 }} size="small" variant="contained" onClick={() => setOpenHotspotOptionForm(true)}>
                    Select asset and zone
                </Button>
            )}

            {hotspotOpt?.asset_id && hotspotOpt?.zone_id && (
                <>
                    <Button
                        sx={{ mt: 1 }}
                        size="small"
                        variant="contained"
                        onClick={() => handleAddHotspotOption(true)}
                    >
                        Add hotspot
                    </Button>

                    <DragDropContext
                        onDragEnd={(result) =>
                            onDragEnd(result, values?.hotspot_options, 'hotspot_options', setFieldValue)
                        }
                    >
                        <Droppable droppableId="droppable-content-nodes-hotspots">
                            {(provided) => {
                                return (
                                    <>
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{ margin: '40px 0' }}
                                        >
                                            {values?.hotspot_options?.map((option, idx) => (
                                                <HotspotAnswerField
                                                    option={option}
                                                    key={`HotspotAnswerField${idx}`}
                                                    idx={idx}
                                                    node={node}
                                                    nodes={nodes}
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    provided={provided}
                                                    handleRemove={(id) => {
                                                        let opt_txt = values.hotspot_options[id].option_text;
                                                        handleRemove(
                                                            id,
                                                            values.hotspot_options,
                                                            'hotspot_options',
                                                            setFieldValue
                                                        );
                                                        handleRemoveOptionsSelected(
                                                            opt_txt,
                                                            values.hotspot_target_combinations,
                                                            'hotspot_target_combinations',
                                                            setFieldValue
                                                        );
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </>
                                );
                            }}
                        </Droppable>
                    </DragDropContext>
                    {values?.multiple_answers_allow === true &&
                        Array.isArray(values?.hotspot_options) &&
                        values?.hotspot_options?.filter((opt) => opt?.item_id)?.length > 1 && (
                            <>
                                <div>
                                    Target Combinations{' '}
                                    <IconButton
                                        className={nodeFormStyles.addButtonIcon}
                                        onClick={() =>
                                            handleAddHotspotPairTarget(
                                                values?.hotspot_target_combinations,
                                                setFieldValue
                                            )
                                        }
                                        size="small"
                                    >
                                        <MdAdd className="react-icon" />
                                    </IconButton>
                                </div>
                                <DragDropContext
                                    onDragEnd={(result) =>
                                        onDragEnd(
                                            result,
                                            values?.hotspot_target_combinations,
                                            'hotspot_target_combinations',
                                            setFieldValue
                                        )
                                    }
                                >
                                    <Droppable droppableId="droppable-content-nodes-hotspots-combinations">
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                style={{
                                                    margin: '40px 0',
                                                }}
                                            >
                                                {values?.hotspot_target_combinations?.map((option, idx) => {
                                                    return (
                                                        <HotspotPairsTargetNode
                                                            option={option}
                                                            selectOptions={values?.hotspot_options?.filter(
                                                                (opt) => opt?.item_id
                                                            )}
                                                            key={`HotspotPairsTargetNode${idx}`}
                                                            idx={idx}
                                                            node={node}
                                                            nodes={nodes}
                                                            values={values}
                                                            setFieldValue={setFieldValue}
                                                            provided={provided}
                                                            handleRemove={(id) =>
                                                                handleRemove(
                                                                    id,
                                                                    values.hotspot_target_combinations,
                                                                    'hotspot_target_combinations',
                                                                    setFieldValue
                                                                )
                                                            }
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </>
                        )}
                </>
            )}
            {openHotspotOptionForm && (
                <HotspotPanoDataForm
                    valuesData={hotspotOpt}
                    setFieldValue={setFieldValue}
                    hotspotOptions={values?.hotspot_options}
                    setOpenHotspotOptionForm={setOpenHotspotOptionForm}
                />
            )}
        </>
    );
};

export default HotspotAnswerGroupType;
